import React, { useState } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { cn } from '../../lib/utils';

function TransactionList({
  transactions,
  loading = false,
  showPagination = true,
  currentPage = 1,
  totalPages = 1,
  onPageChange = () => {},
  showFilters = true,
  onFilterChange = () => {}
}) {
  const [filters, setFilters] = useState({
    type: '',
    dateFrom: '',
    dateTo: ''
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'deposit':
        return '⬇️';
      case 'withdrawal':
        return '⬆️';
      case 'transfer':
        return '↔️';
      case 'contest_entry':
        return '🎯';
      case 'contest_payout':
        return '🏆';
      case 'subsidy':
        return '💰';
      case 'refund':
        return '🔄';
      default:
        return '💳';
    }
  };

  const getTransactionColors = (type) => {
    switch (type) {
      case 'deposit':
      case 'contest_payout':
      case 'subsidy':
        return {
          bg: 'bg-emerald-50 border-emerald-200',
          amount: 'text-emerald-600',
          icon: 'bg-emerald-100'
        };
      case 'withdrawal':
      case 'contest_entry':
        return {
          bg: 'bg-rose-50 border-rose-200',
          amount: 'text-rose-600',
          icon: 'bg-rose-100'
        };
      case 'transfer':
        return {
          bg: 'bg-blue-50 border-blue-200',
          amount: 'text-blue-600',
          icon: 'bg-blue-100'
        };
      case 'refund':
        return {
          bg: 'bg-amber-50 border-amber-200',
          amount: 'text-amber-600',
          icon: 'bg-amber-100'
        };
      default:
        return {
          bg: 'bg-slate-50 border-slate-200',
          amount: 'text-slate-600',
          icon: 'bg-slate-100'
        };
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const renderPagination = () => {
    if (!showPagination || totalPages <= 1) return null;

    const pages = [];
    const maxPagesToShow = 5;
    const startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          onClick={() => onPageChange(i)}
          variant={i === currentPage ? "default" : "outline"}
          size="sm"
          className={cn(
            "min-w-[40px]",
            i === currentPage && "bg-gradient-to-r from-blue-500 to-indigo-600"
          )}
        >
          {i}
        </Button>
      );
    }

    return (
      <div className="flex items-center justify-center gap-2 mt-6 pt-4 border-t border-slate-100">
        {currentPage > 1 && (
          <Button
            onClick={() => onPageChange(currentPage - 1)}
            variant="outline"
            size="sm"
          >
            ← Previous
          </Button>
        )}
        {pages}
        {currentPage < totalPages && (
          <Button
            onClick={() => onPageChange(currentPage + 1)}
            variant="outline"
            size="sm"
          >
            Next →
          </Button>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="flex items-center gap-4 p-4 rounded-lg border border-slate-100">
            <Skeleton className="h-10 w-10 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
            <Skeleton className="h-6 w-20" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {showFilters && (
        <div className="flex flex-wrap items-end gap-4 p-4 bg-slate-50 rounded-lg border border-slate-100">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="type-filter" className="text-xs font-medium text-slate-700">
              Type
            </label>
            <select
              id="type-filter"
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">All Types</option>
              <option value="deposit">Deposit</option>
              <option value="withdrawal">Withdrawal</option>
              <option value="transfer">Transfer</option>
              <option value="contest_entry">Contest Entry</option>
              <option value="contest_payout">Contest Payout</option>
              <option value="subsidy">Subsidy</option>
              <option value="refund">Refund</option>
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="date-from" className="text-xs font-medium text-slate-700">
              From
            </label>
            <input
              type="date"
              id="date-from"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="date-to" className="text-xs font-medium text-slate-700">
              To
            </label>
            <input
              type="date"
              id="date-to"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>

          <Button
            onClick={() => {
              setFilters({ type: '', dateFrom: '', dateTo: '' });
              onFilterChange({ type: '', dateFrom: '', dateTo: '' });
            }}
            variant="ghost"
            size="sm"
            className="text-slate-500 hover:text-slate-700"
          >
            Clear Filters
          </Button>
        </div>
      )}

      {transactions.length === 0 ? (
        <div className="text-center py-12 px-6">
          <div className="text-5xl mb-4">📊</div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No Transactions Found</h3>
          <p className="text-sm text-slate-600">No transactions match your current filters.</p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {transactions.map((transaction) => {
              const colors = getTransactionColors(transaction.type);
              return (
                <div
                  key={transaction._id}
                  className={cn(
                    "flex items-start gap-4 p-4 rounded-lg border transition-all duration-200 hover:shadow-sm",
                    colors.bg
                  )}
                >
                  <div className={cn(
                    "flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-lg",
                    colors.icon
                  )}>
                    {getTransactionIcon(transaction.type)}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">
                          {transaction.description}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs capitalize">
                            {transaction.type.replace('_', ' ')}
                          </Badge>
                          <span className="text-xs text-slate-500">
                            {formatDate(transaction.createdAt)}
                          </span>
                        </div>
                      </div>

                      <div className="text-right flex-shrink-0">
                        <span className={cn("text-lg font-semibold", colors.amount)}>
                          {formatCurrency(transaction.amount)}
                        </span>
                        <div className="mt-1">
                          <Badge
                            variant={transaction.status === 'completed' ? 'success' : 'secondary'}
                            className="text-xs"
                          >
                            {transaction.status}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    {transaction.metadata?.isReversal && (
                      <Badge variant="warning" className="mt-2 text-xs">
                        REVERSAL
                      </Badge>
                    )}

                    {transaction.fromWalletId && transaction.toWalletId && (
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-slate-500">
                        <span>
                          <span className="font-medium">From:</span>{' '}
                          {transaction.fromWalletId?.ownerName || 'Unknown'}
                        </span>
                        <span>
                          <span className="font-medium">To:</span>{' '}
                          {transaction.toWalletId?.ownerName || 'Unknown'}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {renderPagination()}
        </>
      )}
    </div>
  );
}

export default TransactionList;
